import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarDays, Loader2, Plus, Vote } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuth } from '@/context/authContext';
import * as repo from '../data/votingRepository';
import { useElections, useVotingMutation } from '../hooks/useVoting';
import { ElectionFormDialog, type ElectionFormValues } from '../components/ElectionFormDialog';
import { STATUS_LABEL } from '../utils/labels';

export function VotingListPage() {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { user } = useAuth();
  const { data: elections = [], isLoading } = useElections();
  const [creating, setCreating] = useState(false);

  const create = useVotingMutation(undefined, (v: ElectionFormValues) => repo.createElection({ ...v, created_by: user?.id }));

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold"><Vote className="size-6" /> Elecciones</h1>
          <p className="text-sm text-muted-foreground">Cargos, rondas de votación y resultados.</p>
        </div>
        {can('election.create') && (
          <Button onClick={() => setCreating(true)}><Plus className="mr-2 size-4" /> Nueva elección</Button>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="size-6 animate-spin text-muted-foreground" /></div>
      ) : elections.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">Aún no hay elecciones.</CardContent></Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {elections.map((e) => (
            <Card key={e.id} className="cursor-pointer transition-colors hover:bg-muted/40" onClick={() => navigate(`/voting/${e.id}`)}>
              <CardContent className="space-y-2 p-4">
                <div className="flex items-start justify-between gap-2">
                  <h2 className="font-semibold">{e.name}</h2>
                  <Badge variant={e.status === 'ACTIVE' ? 'default' : 'secondary'}>{STATUS_LABEL[e.status as keyof typeof STATUS_LABEL]}</Badge>
                </div>
                <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <CalendarDays className="size-4" /> {e.event?.name ?? 'Sin evento (lista manual)'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {e.election_positions[0]?.count ?? 0} cargo(s) · {e.election_races[0]?.count ?? 0} ronda(s) · {Number(e.threshold_pct)} % para ganar
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <ElectionFormDialog
        open={creating}
        onOpenChange={setCreating}
        busy={create.isPending}
        onSubmit={(v) =>
          create.mutate(v, {
            onSuccess: (el) => {
              setCreating(false);
              navigate(`/voting/${(el as { id: string }).id}`);
            },
          })
        }
      />
    </div>
  );
}
